import React, { useState, useEffect } from 'react';
import { getLogs } from '../../data/logService';
import { formatDateMoscowWithSeconds } from '../../utils/dateUtils';
import { History, Search, RefreshCw } from 'lucide-react';

const PAGE_SIZE = 50;

const LogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const loadLogs = async (pageNum = page) => {
        try {
            setLoading(true);
            setError(null);
            const data = await getLogs(pageNum, PAGE_SIZE, searchTerm);
            setLogs(data.items || []);
            setTotal(data.total ?? 0);
            setTotalPages(data.total_pages ?? 1);
        } catch (err) {
            console.error('Error loading logs:', err);
            setError('Не удалось загрузить логи');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    useEffect(() => {
        loadLogs(page);
    }, [page, searchTerm]);

    const filteredLogs = logs;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return formatDateMoscowWithSeconds(dateString);
    };

    const actionLabels = {
        'buy-self': 'Покупка себе',
        'buy-friend': 'Покупка другу',
        'payment-created': 'Платёж создан',
        'payment-success': 'Успешная оплата',
        'payment-success-friend': 'Успешная оплата (другу)',
    };
    const getActionLabel = (action) => actionLabels[action] || action || '-';

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-3">
                    <History className="text-slate-800" size={28} />
                    <h2 className="text-2xl font-bold text-slate-800">Логи системы</h2>
                </div>
                <button
                    onClick={() => loadLogs(page)}
                    disabled={loading}
                    className="flex items-center space-x-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    <span>Обновить</span>
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                        type="text"
                        placeholder="Поиск по логам..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-center py-12">
                    <RefreshCw className="animate-spin mx-auto text-slate-400" size={32} />
                    <p className="mt-4 text-slate-600">Загрузка логов...</p>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
                    <History className="mx-auto text-slate-300" size={48} />
                    <p className="mt-4 text-slate-600">
                        {searchTerm ? 'Логи не найдены' : 'Логи отсутствуют'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        Дата
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        Действие
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        Сообщение
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {filteredLogs.map((log) => (
                                    <tr key={log.id} className="hover:bg-slate-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                            #{log.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                            {formatDate(log.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                            {log.user_id || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                            <span className={
                                                log.action === 'payment-success' || log.action === 'payment-success-friend'
                                                    ? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800'
                                                    : ''
                                            }>
                                                {getActionLabel(log.action)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-900">
                                            {log.message || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && (filteredLogs.length > 0 || total > 0) && (
                <div className="flex flex-col items-center gap-4">
                    <div className="text-sm text-slate-600">
                        Показано {filteredLogs.length} из {total} логов на странице {page} из {totalPages}
                    </div>
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                            >
                                Назад
                            </button>
                            <span className="px-4 py-2 text-slate-600">
                                Страница {page} из {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                            >
                                Вперед
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LogsPage;
